import React, { useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, Download, X, Loader2 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface DebitNoteCompany {
  Id?: number;
  Name?: string;
  LandPhone?: string;
  Email?: string;
  GSTN?: string;
  Mobile?: string;
  Logo?: string;
  LogoPath?: string;
  Address?: string;
  Address1?: string;
  Address2?: string;
  Address3?: string;
  City?: string;
  State?: string;
  PinZip?: string;
  Country?: string;
}

export interface DebitNoteSupplier {
  ID?: number;
  Name?: string;
  Phone?: string;
  Email?: string;
  Address?: string;
  State?: string;
  ContactName?: string;
  ContactMobile?: string;
  GSTN?: string;
  DebitNoteRequestNo?: string;
  Date?: string;
}

export interface DebitNoteItem {
  SNo: number;
  ProductName: string;
  UnitPrice: number;
  Qty: number;
  Disc: number;
  SGST: number;
  CGST: number;
  IGST: number;
  TotalPrice: number;
}

export interface DebitNotePreviewData {
  Company: DebitNoteCompany;
  Supplier: DebitNoteSupplier;
  Items: DebitNoteItem[];
  TotalRecords?: number;
}

function formatNum(n: number): string {
  return typeof n === 'number' && !Number.isNaN(n)
    ? n.toFixed(2)
    : '0.00';
}

function escapeHtml(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

/** Build company address from Address1, Address2, Address3, City, State, PinZip, Country; fallback to Address */
function getCompanyAddressDisplay(company: DebitNoteCompany | undefined | null): string {
  if (!company) return '';
  const parts = [
    company.Address1,
    company.Address2,
    company.Address3,
    company.City,
    company.State,
    company.PinZip,
    company.Country,
  ].filter(Boolean) as string[];
  if (parts.length > 0) return parts.join(', ');
  return company.Address ?? '';
}

/** Convert API logo (raw base64 or data URL) to data URL for img src */
function getCompanyLogoDataUrl(logoBase64: string | undefined): string | null {
  if (!logoBase64 || typeof logoBase64 !== 'string') return null;
  const s = logoBase64.trim();
  if (s.startsWith('data:image')) return s;
  if (s.length > 0) return `data:image/jpeg;base64,${s}`;
  return null;
}

/** Fetch image from URL and return as base64 data URL, or null if CORS/fail */
function getImageAsBase64(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    if (!url || !url.startsWith('http')) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

interface DebitNotePreviewProps {
  open: boolean;
  onClose: () => void;
  data: DebitNotePreviewData | null;
  isLoading?: boolean;
  error?: string | null;
  onPrint?: () => void;
  onDownloadPdf?: () => void;
  isPrinting?: boolean;
  isDownloading?: boolean;
}

export const DebitNotePreview: React.FC<DebitNotePreviewProps> = ({
  open,
  onClose,
  data,
  isLoading,
  error,
  onPrint,
  onDownloadPdf,
  isPrinting,
  isDownloading,
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    if (onPrint) {
      onPrint();
      return;
    }
    if (!data) return;

    const companyName = data.Company?.Name ?? '';
    const companyGst = data.Company?.GSTN ?? '';
    const companyAddress = getCompanyAddressDisplay(data.Company);
    const companyPhone = [data.Company?.Mobile, data.Company?.LandPhone].filter(Boolean).join(', ') || '';
    const debitNo = data.Supplier?.DebitNoteRequestNo ?? '';
    const logoDataUrl = getCompanyLogoDataUrl(data.Company?.Logo);
    const items = data.Items ?? [];
    const totalQty = items.reduce((s, i) => s + (i.Qty ?? 0), 0);
    const totalDisc = items.reduce((s, i) => s + (i.Disc ?? 0), 0);
    const totalSgst = items.reduce((s, i) => s + (i.SGST ?? 0), 0);
    const totalCgst = items.reduce((s, i) => s + (i.CGST ?? 0), 0);
    const totalIgst = items.reduce((s, i) => s + (i.IGST ?? 0), 0);
    const totalPrice = items.reduce((s, i) => s + (i.TotalPrice ?? 0), 0);

    const cellPad = '4px 6px';
    const companyRows = `
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold;width:140px">Company</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(companyName)}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">GST No</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(companyGst)}</td></tr>
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Address</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(companyAddress) || '-'}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Phone</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(companyPhone) || '-'}</td></tr>`;
    const supplier = data.Supplier;
    const supplierRows = `
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Supplier Name</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.Name ?? '')}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Phone</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.Phone ?? '')}</td></tr>
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Address</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.Address ?? '')}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">State</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.State ?? '')}</td></tr>
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Contact Name</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.ContactName ?? '')}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">GST No.</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.GSTN ?? '')}</td></tr>
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Contact Number</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.ContactMobile ?? '')}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Debit Note Request No.</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.DebitNoteRequestNo ?? '')}</td></tr>
      <tr><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Date</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.Date ?? '')}</td><td style="border:1px solid #000;padding:${cellPad};font-weight:bold">Email</td><td style="border:1px solid #000;padding:${cellPad}">${escapeHtml(supplier?.Email ?? '')}</td></tr>`;
    const emptyRowHtml = '<tr><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px"></td></tr>';
    const ITEMS_PER_PAGE = 18; // 18 items + empty + 1 total - keeps total on same page (avoids overflow)
    const emptyRowsForPage = (itemCount: number) => Math.max(0, ITEMS_PER_PAGE - itemCount - 1);
    const itemRowHtml = (row: DebitNoteItem) => `<tr>
      <td style="border:1px solid #000;padding:4px;text-align:center">${row.SNo}</td>
      <td style="border:1px solid #000;padding:4px">${escapeHtml(row.ProductName ?? '')}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.UnitPrice)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.Qty)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.Disc)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.SGST)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.CGST)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.IGST)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(row.TotalPrice)}</td>
    </tr>`;
    const totalRow = `<tr style="font-weight:bold">
      <td style="border:1px solid #000;padding:4px"></td><td style="border:1px solid #000;padding:4px">Total</td><td style="border:1px solid #000;padding:4px"></td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalQty)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalDisc)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalSgst)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalCgst)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalIgst)}</td>
      <td style="border:1px solid #000;padding:4px;text-align:right">${formatNum(totalPrice)}</td>
    </tr>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.print();
      return;
    }
    const logoImgHtml = logoDataUrl
      ? `<div style="width:140px;display:flex;align-items:center;min-height:1px;"><img src="${escapeHtml(logoDataUrl)}" alt="" style="max-width:130px;max-height:56px;height:56px;object-fit:contain;" /></div>`
      : '<div style="width:140px"></div>';
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Debit Note - ${escapeHtml(debitNo)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 12px; padding: 15mm; color: #000; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; min-height: 56px; }
            .company-name { flex: 1; text-align: center; font-weight: bold; font-size: 16px; text-transform: uppercase; line-height: 1.2; }
            .debit-content { max-width: 180mm; margin: 0 auto; min-height: 100vh; display: flex; flex-direction: column; }
            .debit-content .page-top { flex: 1; }
            .debit-content .page-bottom { margin-top: auto; padding-top: 8px; }
            .page-break { page-break-before: always; }
            .title-bar { background: #e5e7eb; border: 1px solid #000; padding: 4px; text-align: center; margin-bottom: 4px; font-weight: bold; text-transform: uppercase; width: 100%; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
            table td, table th { border: 1px solid #000; padding: 4px 6px; }
            table.items th { background: #4b5563; color: #fff; font-weight: bold; text-align: center; }
            table.items { page-break-inside: avoid; }
            table.items tr:last-child { page-break-after: avoid; }
            .footer { margin-top: 8px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { padding: 2px 10mm 8mm 10mm; }
              @page { size: A4; margin: 6mm 12mm 10mm 12mm; }
            }
          </style>
        </head>
        <body>
          <div class="debit-content">
            <div class="page-top">
              <div class="header">
                ${logoImgHtml}
                <div class="company-name">${escapeHtml(companyName)}</div>
                <div style="width:140px"></div>
              </div>
              <div class="title-bar">DEBIT NOTE REQUEST</div>
              <table><tbody>${companyRows}</tbody></table>
              <table><tbody>${supplierRows}</tbody></table>
              ${items.length <= ITEMS_PER_PAGE
                ? `<table class="items">
                  <thead><tr>
                    <th>S No.</th><th>Product Name</th><th>Unit Price</th><th>Qty</th><th>Disc.</th><th>SGST</th><th>CGST</th><th>IGST</th><th>Total Price</th>
                  </tr></thead>
                  <tbody>
                    ${items.map(itemRowHtml).join('')}
                    ${emptyRowHtml.repeat(emptyRowsForPage(items.length))}
                    ${totalRow}
                  </tbody>
                </table>`
              : (() => {
                  let html = '';
                  for (let i = 0; i < items.length; i += ITEMS_PER_PAGE) {
                    const chunk = items.slice(i, i + ITEMS_PER_PAGE);
                    const isLastPage = i + ITEMS_PER_PAGE >= items.length;
                    const emptyCount = isLastPage ? emptyRowsForPage(chunk.length) : 0;
                    if (i > 0) html += '<div class="page-break"></div>';
                    html += `<table class="items">
                      <thead><tr>
                        <th>S No.</th><th>Product Name</th><th>Unit Price</th><th>Qty</th><th>Disc.</th><th>SGST</th><th>CGST</th><th>IGST</th><th>Total Price</th>
                      </tr></thead>
                      <tbody>
                        ${chunk.map(itemRowHtml).join('')}
                        ${emptyRowHtml.repeat(emptyCount)}
                        ${isLastPage ? totalRow : ''}
                      </tbody>
                    </table>`;
                  }
                  return html;
                })()
              }
            </div>
            <div class="page-bottom">
              <div class="footer">Debit Note - ${escapeHtml(debitNo)} | Generated on ${new Date().toLocaleDateString('en-IN')}</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, [data, onPrint]);

  const handleDownloadPdf = useCallback(async () => {
    if (onDownloadPdf) {
      onDownloadPdf();
      return;
    }
    if (!data) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    let y = margin;

    const companyName = data.Company?.Name ?? '';
    const companyGst = data.Company?.GSTN ?? '';
    const companyAddress = getCompanyAddressDisplay(data.Company);
    const companyPhone = [data.Company?.Mobile, data.Company?.LandPhone].filter(Boolean).join(', ') || '';
    const debitNo = data.Supplier?.DebitNoteRequestNo ?? '';
    const logoDataUrl = getCompanyLogoDataUrl(data.Company?.Logo);

    const headerLineHeight = 22; // Logo and company name share same line - bigger logo
    if (logoDataUrl) {
      try {
        const img = new Image();
        img.src = logoDataUrl;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Logo load failed'));
        });
        const maxLogoW = 55;
        const maxLogoH = headerLineHeight;
        let lw = img.width;
        let lh = img.height;
        if (lw > maxLogoW) {
          const scale = maxLogoW / lw;
          lw = maxLogoW;
          lh = lh * scale;
        }
        if (lh > maxLogoH) {
          const scale = maxLogoH / lh;
          lh = maxLogoH;
          lw = lw * scale;
        }
        const imgFormat = logoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(logoDataUrl, imgFormat, margin, y, lw, lh);
      } catch {
        // continue without logo
      }
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    const cnWidth = doc.getTextWidth(companyName);
    doc.text(companyName, (pageWidth - cnWidth) / 2, y + headerLineHeight * 0.7);
    y += headerLineHeight + 2;

    // DEBIT NOTE REQUEST bar
    doc.setFillColor(229, 231, 235);
    doc.rect(margin, y, contentWidth, 6, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.rect(margin, y, contentWidth, 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DEBIT NOTE REQUEST', margin + contentWidth / 2, y + 4, { align: 'center' });
    y += 8;

    const sup = data.Supplier ?? {};
    const borderStyle = { lineColor: [0, 0, 0] as [number, number, number], lineWidth: 0.3 };

    // Company info table - bordered grid so PDF matches preview
    autoTable(doc, {
      startY: y,
      body: [
        ['Company', companyName, 'GST No', companyGst],
        ['Address', companyAddress || '-', 'Phone', companyPhone || '-'],
      ],
      theme: 'grid',
      margin: { left: margin, right: margin },
      tableWidth: 'auto',
      styles: { fontSize: 9, cellPadding: 2, ...borderStyle },
      bodyStyles: { ...borderStyle },
      columnStyles: {
        0: { cellWidth: 28, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 28, fontStyle: 'bold' },
        3: { cellWidth: 'auto' },
      },
    });
    y = (doc as any).lastAutoTable?.finalY ?? y;
    y += 2;

    // Supplier info table - bordered grid so PDF matches preview
    autoTable(doc, {
      startY: y,
      body: [
        ['Supplier Name', sup.Name ?? '', 'Phone', sup.Phone ?? ''],
        ['Address', sup.Address ?? '', 'State', sup.State ?? ''],
        ['Contact Name', sup.ContactName ?? '', 'GST No.', sup.GSTN ?? ''],
        ['Contact Number', sup.ContactMobile ?? '', 'Debit Note Request No.', sup.DebitNoteRequestNo ?? ''],
        ['Date', sup.Date ?? '', 'Email', sup.Email ?? ''],
      ],
      theme: 'grid',
      margin: { left: margin, right: margin },
      tableWidth: 'auto',
      styles: { fontSize: 9, cellPadding: 2, ...borderStyle },
      bodyStyles: { ...borderStyle },
      columnStyles: {
        0: { cellWidth: 42, fontStyle: 'bold' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 42, fontStyle: 'bold' },
        3: { cellWidth: 'auto' },
      },
    });
    y = (doc as any).lastAutoTable?.finalY ?? y;
    y += 4;

    const items = data.Items ?? [];
    const totalQty = items.reduce((s, i) => s + (i.Qty ?? 0), 0);
    const totalDisc = items.reduce((s, i) => s + (i.Disc ?? 0), 0);
    const totalSgst = items.reduce((s, i) => s + (i.SGST ?? 0), 0);
    const totalCgst = items.reduce((s, i) => s + (i.CGST ?? 0), 0);
    const totalIgst = items.reduce((s, i) => s + (i.IGST ?? 0), 0);
    const totalPrice = items.reduce((s, i) => s + (i.TotalPrice ?? 0), 0);

    const itemsBody = items.map((row) => [
      String(row.SNo),
      row.ProductName ?? '',
      formatNum(row.UnitPrice),
      formatNum(row.Qty),
      formatNum(row.Disc),
      formatNum(row.SGST),
      formatNum(row.CGST),
      formatNum(row.IGST),
      formatNum(row.TotalPrice),
    ]);
    const totalsRow = ['', 'Total', '', formatNum(totalQty), formatNum(totalDisc), formatNum(totalSgst), formatNum(totalCgst), formatNum(totalIgst), formatNum(totalPrice)];

    const ITEMS_PER_PAGE = 18; // 18 items + empty + 1 total - keeps total on same page (avoids overflow)

    const emptyRow: (string | number)[] = ['', '', '', '', '', '', '', '', ''];
    const contentWidthMm = contentWidth;
    const colWidths = [10, 44, 22, 16, 14, 18, 18, 16, 22];
    const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
    if (Math.abs(totalColWidth - contentWidthMm) > 1) {
      colWidths[1] = Math.max(20, contentWidthMm - (totalColWidth - colWidths[1]));
    }

    const drawFooter = (pageY: number) => {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Debit Note - ${debitNo} | Generated on ${new Date().toLocaleDateString('en-IN')}`,
        pageWidth / 2,
        Math.min(pageY + 10, pageHeight - 10),
        { align: 'center' }
      );
    };

    const tableOpts = {
      theme: 'grid' as const,
      margin: { left: margin, right: margin },
      tableWidth: contentWidthMm,
      styles: { fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2 },
      headStyles: { fillColor: [75, 85, 99], textColor: 255, fontStyle: 'bold', lineColor: [0, 0, 0], lineWidth: 0.3 },
      bodyStyles: { lineColor: [0, 0, 0], lineWidth: 0.3 },
      columnStyles: {
        0: { cellWidth: colWidths[0], halign: 'center' as const },
        1: { cellWidth: colWidths[1], halign: 'left' as const },
        2: { cellWidth: colWidths[2], halign: 'right' as const },
        3: { cellWidth: colWidths[3], halign: 'right' as const },
        4: { cellWidth: colWidths[4], halign: 'right' as const },
        5: { cellWidth: colWidths[5], halign: 'right' as const },
        6: { cellWidth: colWidths[6], halign: 'right' as const },
        7: { cellWidth: colWidths[7], halign: 'right' as const },
        8: { cellWidth: colWidths[8], halign: 'right' as const },
      },
    };

    // ≤18 items: items + empty rows + total (one table). >18: total on last page with fill
    // Empty rows = 18 - itemCount - 1 (for total row) - conservative to avoid total on next page
    const emptyRowsForPage = (itemCount: number) => Math.max(0, ITEMS_PER_PAGE - itemCount - 1);
    if (items.length <= ITEMS_PER_PAGE) {
      const numEmpty = emptyRowsForPage(items.length);
      const emptyRows = Array(numEmpty).fill(emptyRow) as (string | number)[][];
      const productTableBody = itemsBody.concat(emptyRows).concat([totalsRow]);
      const totalsRowIndex = items.length + numEmpty;

      autoTable(doc, {
        ...tableOpts,
        startY: y,
        head: [['S No.', 'Product Name', 'Unit Price', 'Qty', 'Disc.', 'SGST', 'CGST', 'IGST', 'Total Price']],
        body: productTableBody,
        rowStyles: { [totalsRowIndex]: { fontStyle: 'bold' } },
      });
      const finalY = (doc as any).lastAutoTable?.finalY ?? y;
      drawFooter(finalY);
    } else {
      let start = 0;
      while (start < items.length) {
        const isLastPage = start + ITEMS_PER_PAGE >= items.length;
        const chunk = itemsBody.slice(start, start + ITEMS_PER_PAGE);

        if (isLastPage) {
          const numEmpty = emptyRowsForPage(chunk.length);
          const emptyRows = Array(numEmpty).fill(emptyRow) as (string | number)[][];
          const productTableBody = chunk.concat(emptyRows).concat([totalsRow]);
          const totalsRowIndex = chunk.length + numEmpty;

          autoTable(doc, {
            ...tableOpts,
            startY: y,
            head: [['S No.', 'Product Name', 'Unit Price', 'Qty', 'Disc.', 'SGST', 'CGST', 'IGST', 'Total Price']],
            body: productTableBody,
            rowStyles: { [totalsRowIndex]: { fontStyle: 'bold' } },
          });
          const finalY = (doc as any).lastAutoTable?.finalY ?? y;
          drawFooter(finalY);
        } else {
          autoTable(doc, {
            ...tableOpts,
            startY: y,
            head: [['S No.', 'Product Name', 'Unit Price', 'Qty', 'Disc.', 'SGST', 'CGST', 'IGST', 'Total Price']],
            body: chunk,
          });
          doc.addPage();
          y = margin;
        }
        start += ITEMS_PER_PAGE;
      }
    }

    doc.save(`DebitNote_${(debitNo || 'debit-note').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`);
  }, [data, onDownloadPdf]);

  const companyName = data?.Company?.Name ?? '';
  const companyGst = data?.Company?.GSTN ?? '';
  const companyAddress = getCompanyAddressDisplay(data?.Company);
  const companyPhone = [data?.Company?.Mobile, data?.Company?.LandPhone].filter(Boolean).join(', ') || '';
  const logoDataUrl = getCompanyLogoDataUrl(data?.Company?.Logo);

  const items = data?.Items ?? [];
  const totalQty = items.reduce((s, i) => s + (i.Qty ?? 0), 0);
  const totalDisc = items.reduce((s, i) => s + (i.Disc ?? 0), 0);
  const totalSgst = items.reduce((s, i) => s + (i.SGST ?? 0), 0);
  const totalCgst = items.reduce((s, i) => s + (i.CGST ?? 0), 0);
  const totalIgst = items.reduce((s, i) => s + (i.IGST ?? 0), 0);
  const totalPrice = items.reduce((s, i) => s + (i.TotalPrice ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Debit Note Preview</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )}

          {error && (
            <div className="py-4 text-center text-red-600">{error}</div>
          )}

          {!isLoading && !error && data && (
            <>
              <div className="flex-1 overflow-auto border rounded bg-white p-4 mb-4" style={{ minHeight: 400 }}>
                <div ref={printRef} className="debit-note-content bg-white text-black text-sm max-w-full flex flex-col" style={{ maxWidth: '180mm', minHeight: 400 }}>
                  <div className="flex-1 flex flex-col">
                  {/* Header: Company logo and company name - bigger logo */}
                  <div className="flex justify-between items-center mb-2 min-h-[56px]">
                    <div className="w-36 flex items-center justify-start shrink-0">
                      {logoDataUrl && (
                        <img src={logoDataUrl} alt="" className="max-w-[130px] max-h-[56px] h-14 object-contain" />
                      )}
                    </div>
                    <div className="flex-1 text-center font-bold text-base uppercase">
                      {companyName}
                    </div>
                    <div className="w-32" />
                  </div>

                  {/* Title bar */}
                  <div className="bg-gray-200 border border-black py-1.5 text-center font-bold uppercase text-sm mb-2">
                    DEBIT NOTE REQUEST
                  </div>

                  {/* Company info table - board style */}
                  <table className="w-full mb-3" style={{ border: '1px solid #000', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td className="p-2 font-bold w-36" style={{ border: '1px solid #000' }}>Company</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{companyName}</td>
                        <td className="p-2 font-bold w-24" style={{ border: '1px solid #000' }}>GST No</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{companyGst}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Address</td>
                        <td className="p-2 whitespace-pre-wrap" style={{ border: '1px solid #000' }}>{companyAddress || '-'}</td>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Phone</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{companyPhone || '-'}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Supplier info table - board table (bordered every cell) */}
                  <table className="w-full mb-3" style={{ border: '1px solid #000', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td className="p-2 font-bold w-36" style={{ border: '1px solid #000' }}>Supplier Name</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.Name ?? ''}</td>
                        <td className="p-2 font-bold w-24" style={{ border: '1px solid #000' }}>Phone</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.Phone ?? ''}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Address</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.Address ?? ''}</td>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>State</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.State ?? ''}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Contact Name</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.ContactName ?? ''}</td>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>GST No.</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.GSTN ?? ''}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Contact Number</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.ContactMobile ?? ''}</td>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Debit Note Request No.</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.DebitNoteRequestNo ?? ''}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Date</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.Date ?? ''}</td>
                        <td className="p-2 font-bold" style={{ border: '1px solid #000' }}>Email</td>
                        <td className="p-2" style={{ border: '1px solid #000' }}>{data.Supplier?.Email ?? ''}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Items table - total row inside table. ≤24: same page; >24: total on last page with fill */}
                  {(() => {
                    const ITEMS_PER_PAGE = 18;
                    const emptyRowsCount = items.length <= ITEMS_PER_PAGE
                      ? Math.max(0, ITEMS_PER_PAGE - items.length - 1)
                      : Math.max(0, ITEMS_PER_PAGE - (items.length % ITEMS_PER_PAGE || ITEMS_PER_PAGE) - 1);
                    const cellCls = 'p-2';
                    const borderCls = { border: '1px solid #000' } as const;
                    return (
                      <table className="w-full" style={{ border: '1px solid #000', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr className="bg-gray-600 text-white">
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>S No.</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>Product Name</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>Unit Price</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>Qty</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>Disc.</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>SGST</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>CGST</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>IGST</th>
                            <th className={`${cellCls} text-center font-bold`} style={borderCls}>Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => (
                            <tr key={row.SNo}>
                              <td className={`${cellCls} text-center`} style={borderCls}>{row.SNo}</td>
                              <td className={cellCls} style={borderCls}>{row.ProductName ?? ''}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.UnitPrice)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.Qty)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.Disc)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.SGST)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.CGST)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.IGST)}</td>
                              <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(row.TotalPrice)}</td>
                            </tr>
                          ))}
                          {Array.from({ length: emptyRowsCount }).map((_, i) => (
                            <tr key={`empty-${i}`}>
                              {Array.from({ length: 9 }).map((_, j) => <td key={j} className={cellCls} style={borderCls} />)}
                            </tr>
                          ))}
                          <tr className="font-semibold">
                            <td className={cellCls} style={borderCls}></td>
                            <td className={cellCls} style={borderCls}>Total</td>
                            <td className={cellCls} style={borderCls}></td>
                            <td className={`${cellCls} text-right font-medium`} style={borderCls}>{formatNum(totalQty)}</td>
                            <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(totalDisc)}</td>
                            <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(totalSgst)}</td>
                            <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(totalCgst)}</td>
                            <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(totalIgst)}</td>
                            <td className={`${cellCls} text-right`} style={borderCls}>{formatNum(totalPrice)}</td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })()}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end flex-shrink-0 no-print">
                <Button type="button" variant="outline" onClick={onClose}>
                  <X className="h-4 w-4 mr-2" />
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={handlePrint}
                  disabled={isPrinting}
                  className="bg-slate-700 hover:bg-slate-800"
                >
                  {isPrinting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                  Print
                </Button>
                <Button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isDownloading}
                  className="bg-slate-700 hover:bg-slate-800"
                >
                  {isDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Download PDF
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DebitNotePreview;
